import React from "react";

import { Icon } from "./icons";
import { Menu } from "./Menu";
import { type Layer } from "./Tabs";
import { InfoDot } from "./Tooltip";

/** Page header and an unframed workspace. A page's views are picked from
 * the sidebar's hover panel; the title's crumb becomes a menu only where that
 * panel can't open (touch screens, phone widths). */
export function Page<T extends string>({
  title,
  scope,
  modes,
  actions,
  layers,
  children,
}: {
  title: string;
  /** Whose data this is, only when that isn't obvious. */
  scope?: string;
  /** A Segmented control for the page's two to five modes. */
  modes?: React.ReactNode;
  /** Range picker and page actions, right-aligned. */
  actions?: React.ReactNode;
  layers?: {
    value: T;
    options: Layer<T>[];
    onChange: (v: T) => void;
    title?: string;
  };
  children: React.ReactNode;
}) {
  const active = layers?.options.find((o) => o.value === layers.value)?.label;
  return (
    <ActiveTab.Provider value={active}>
      <div className="ds-page">
        <header className="ds-page-head">
          <h1 className="ds-page-title">
            {title}
            {active && layers && (
              <span className="ds-page-crumb">
                <Icon name="chevron-right" size={16} />
                {layers.options.length > 1 ? (
                  <>
                    <span className="ds-page-current">{active}</span>
                    <Menu
                      label={layers.title ?? "Views"}
                      align="start"
                      items={layers.options.map((o) => ({
                        label:
                          o.count != null ? `${o.label} (${o.count})` : o.label,
                        icon: o.icon,
                        checked: o.value === layers.value,
                        onSelect: () => layers.onChange(o.value),
                      }))}
                      trigger={(p) => (
                        <button
                          type="button"
                          className="ds-page-switch"
                          aria-label={`${layers.title ?? "View"}: ${active}`}
                          ref={p.ref}
                          onClick={p.onClick}
                          aria-haspopup={p["aria-haspopup"]}
                          aria-expanded={p["aria-expanded"]}
                        >
                          {active}
                          <Icon name="chevron-down" size={16} />
                        </button>
                      )}
                    />
                  </>
                ) : (
                  active
                )}
              </span>
            )}
            {scope && <span className="ds-page-scope">{scope}</span>}
          </h1>
          <div className="ds-page-modes">{modes}</div>
          <div className="ds-page-actions">{actions}</div>
        </header>
        <div className="ds-sheet flat">
          <div className="ds-sheet-body">{children}</div>
        </div>
      </div>
    </ActiveTab.Provider>
  );
}

const ActiveTab = React.createContext<string | undefined>(undefined);

/** Title row inside the sheet: names the current layer, controls on the right.
 * The title is dropped when it only repeats the view selected in the panel. */
export function SectionHead({
  title,
  info,
  sub,
  actions,
}: {
  title: React.ReactNode;
  info?: string;
  sub?: React.ReactNode;
  actions?: React.ReactNode;
}) {
  // A title that repeats the panel goes, unless it is the only place left
  // for its info.
  const repeat = React.useContext(ActiveTab) === title && (!!sub || !info);
  if (repeat && !sub && !actions) return null;
  return (
    <div className="ds-section-head">
      <div>
        {!repeat && (
          <h2 className="ds-section-title">
            {title}
            {info && <InfoDot text={info} />}
          </h2>
        )}
        {sub && (
          <p className="ds-section-sub">
            {sub}
            {repeat && info && (
              <>
                {" "}
                <InfoDot text={info} />
              </>
            )}
          </p>
        )}
      </div>
      {actions && <div className="ds-section-actions">{actions}</div>}
    </div>
  );
}

/** Responsive grid of equal columns that drops to one on phones. */
export function Grid({
  cols = 3,
  children,
  className,
}: {
  cols?: 2 | 3 | 4 | 5 | 6;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`ds-grid c${cols}${className ? ` ${className}` : ""}`}>
      {children}
    </div>
  );
}
